import { NavLink } from "@remix-run/react";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import className from "~/kernel/styling/className";
import isPresent from "~/kernel/validation/isPresent";
import useEffectOnce from "~/kernel/effects/useEffectOnce";
import Icon from "./Icon";

type LinkDef = {
  to: string;
  children: React.ReactNode;
};

export default function HamburgerMenu({ links }: { links: LinkDef[] }) {
  const [isActive, setIsActive] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffectOnce(() => {
    const closeMenu = (event: MouseEvent) => {
      const elements = [menuRef.current, buttonRef.current].filter(isPresent);
      const isInside = elements.some((el) => el.contains(event.target as Node));
      const isOutside = !isInside;

      if (isOutside) {
        setIsActive(false);
      }
    };

    document.addEventListener("click", closeMenu);
    return () => document.removeEventListener("click", closeMenu);
  });

  return (
    <>
      <button
        ref={buttonRef}
        id="menu-toggle"
        aria-label="Toggle Menu"
        className="justify-self-end px-3 py-2 md:hidden"
        onClick={() => setIsActive(!isActive)}
      >
        <Icon name="bars" className="text-2xl" />
      </button>

      <div
        ref={menuRef}
        className={className(
          "fixed right-0 z-10 h-screen w-screen bg-white shadow-lg transition-all top-0",
          {
            "translate-x-[10vw]": isActive,
            "translate-x-[100vw]": !isActive,
          },
        )}
      >
        <div className="flex justify-end pr-[12vw] h-16 items-center">
          <button onClick={() => setIsActive(false)} className="p-3">
            <Icon name="x" className="text-2xl" />
          </button>
        </div>

        <ul>
          {isActive &&
            links.map((link) => {
              return (
                <HamburgerMenuLink to={link.to} key={link.to}>
                  {link.children}
                </HamburgerMenuLink>
              );
            })}
        </ul>
      </div>
    </>
  );
}

function HamburgerMenuLink({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) {
  const klass = "px-8 py-5 inline-block w-full border-slate-200 border-b";

  return (
    <li className="relative">
      <NavLink className={klass} to={to}>
        {({ isActive }) =>
          isActive ? (
            <>
              {children}
              <div className="absolute bottom-0 left-0 h-full w-1 bg-primary" />
            </>
          ) : (
            children
          )
        }
      </NavLink>
    </li>
  );
}
